import { ReactNode } from "react";
import { Package } from "lucide-react";
import { ChartTooltipContent, GappedChart, cn } from "web-design-system";
import { ExternalHoldings, PortfolioReviewBlock } from "creso-core/types";
import { convertToCurrency, formatAsPercentage } from "creso-core/utils";
import { AmcLogo } from "@core/components/logo/amc-logo";
import {
  ReviewBlockState,
  ReviewEmptyState,
  ReviewSection,
} from "./review-section";

const formatPct = (value: string | number) => formatAsPercentage(value, "-", 0);

export function ExternalHoldingsSection({
  block,
  isLoading,
}: {
  block?: PortfolioReviewBlock<ExternalHoldings>;
  isLoading: boolean;
}) {
  const holdings = block?.data;

  return (
    <ReviewSection title="External holdings">
      <ReviewBlockState
        isLoading={isLoading}
        isError={block?.ok === false}
        isEmpty={!!holdings && !Number(holdings.external_aum)}
        emptyState={
          <ReviewEmptyState icon={<Package />}>
            No holdings found under other brokers
          </ReviewEmptyState>
        }
        className="gap-8"
      >
        {holdings ? (
          <>
            <ExternalHoldingsSummary holdings={holdings} />
            {holdings.ongoing_sips.sip_count ? (
              <div className="flex flex-col gap-4">
                <h6>Ongoing external SIPs</h6>
                <ExternalSipsSummary sips={holdings.ongoing_sips} />
              </div>
            ) : null}
          </>
        ) : null}
      </ReviewBlockState>
    </ReviewSection>
  );
}

const SPLIT = [
  { key: "regular", label: "Regular", color: "var(--color-chart-1)" },
  { key: "direct", label: "Direct", color: "var(--color-chart-3)" },
] as const;

// Shared with the review report, which prints only the regular/direct split
// and the brokers as a static bar, without the totals or tooltips.
export function ExternalHoldingsSummary({
  holdings,
  variant = "page",
}: {
  holdings: ExternalHoldings;
  variant?: "page" | "report";
}) {
  const isReport = variant === "report";
  const split = SPLIT.map((part) => ({
    ...part,
    value: holdings[`${part.key}_value`],
    pct: Number(holdings[`${part.key}_pct`]),
  }));

  return (
    <div className="flex flex-col gap-6">
      <div
        className={cn(
          "gap-4 rounded-lg bg-card px-4 py-3",
          isReport
            ? "grid grid-cols-2"
            : "flex flex-wrap items-center justify-between"
        )}
      >
        {isReport ? null : (
          <SummaryStat label="External AUM">
            {convertToCurrency(holdings.external_aum)}
          </SummaryStat>
        )}
        {split.map((part) => (
          <SummaryStat key={part.key} label={part.label} accent={part.color}>
            {convertToCurrency(part.value)} ({formatPct(part.pct)})
          </SummaryStat>
        ))}
        {isReport ? null : (
          <SummaryStat label="Funds">
            {holdings.fund_count} funds ({holdings.amc_count} AMCs)
          </SummaryStat>
        )}
      </div>
      {isReport ? (
        <div className="flex h-4 w-full gap-1">
          {split
            .filter((part) => part.pct > 0)
            .map((part) => (
              <div
                key={part.key}
                className="h-full basis-0 rounded-full"
                style={{ flexGrow: part.pct, background: part.color }}
              />
            ))}
        </div>
      ) : (
        <GappedChart
          className="relative h-6 w-full"
          segments={split.map(({ key, label, pct, color }) => ({
            key,
            label,
            value: pct,
            color,
          }))}
          tooltipContent={
            <ChartTooltipContent
              hideLabel
              valueFormatter={(v) => formatPct(Number(v))}
            />
          }
        />
      )}
      <ReviewTable
        columns={["Broker", "AUM", "Funds"]}
        firstColumnClassName={isReport ? "w-1/2" : "w-90"}
        rows={holdings.brokers.map((broker) => ({
          key: broker.broker_code,
          cells: [
            <div key="name" className="flex flex-col gap-1">
              <span>{broker.broker_name}</span>
              <span className="text-xs text-muted-foreground">
                {broker.broker_code}
              </span>
            </div>,
            convertToCurrency(broker.aum),
            broker.fund_count,
          ],
        }))}
      />
    </div>
  );
}

export function ExternalSipsSummary({
  sips,
}: {
  sips: ExternalHoldings["ongoing_sips"];
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-12 rounded-lg bg-card px-4 py-3">
        <SummaryStat label="Monthly SIP">
          {convertToCurrency(sips.monthly_amount)}
        </SummaryStat>
        <SummaryStat label="No. of SIPs">{sips.sip_count}</SummaryStat>
      </div>
      <ReviewTable
        columns={["Fund Name", "SIP amount"]}
        rows={sips.schemes.map((scheme) => ({
          key: scheme.scheme_isin,
          cells: [
            <div key="name" className="flex items-center gap-3">
              <AmcLogo amc_code={scheme.amc_morningstar_code} />
              <div className="flex min-w-0 flex-col gap-1">
                <span className="truncate">
                  {scheme.scheme_name ?? scheme.scheme_isin}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {scheme.category}
                </span>
              </div>
            </div>,
            convertToCurrency(scheme.sip_amount),
          ],
        }))}
      />
    </div>
  );
}

function SummaryStat({
  label,
  accent,
  children,
}: {
  label: string;
  accent?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn("flex flex-col gap-2", accent && "border-l-4 pl-3")}
      style={accent ? { borderColor: accent } : undefined}
    >
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold">{children}</span>
    </div>
  );
}

// First column is left-aligned, the rest are numeric and right-aligned.
function ReviewTable({
  columns,
  rows,
  firstColumnClassName = "w-90",
}: {
  columns: string[];
  rows: { key: string; cells: ReactNode[] }[];
  firstColumnClassName?: string;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full min-w-120 text-sm">
        <thead className="bg-card">
          <tr>
            {columns.map((column, index) => (
              <th
                key={column}
                className={cn(
                  "p-4 font-semibold",
                  index === 0
                    ? cn("text-left", firstColumnClassName)
                    : "text-right"
                )}
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-t border-border">
              {row.cells.map((cell, index) => (
                <td
                  key={columns[index]}
                  className={cn(
                    "px-4 py-3",
                    index === 0 ? "text-left" : "text-right"
                  )}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
